/**
 * Persist the last Reporting left-pane item (new report / inbox id).
 * Scoped per organization + signed-in staff — same keying as the composer draft.
 */

import { complianceComposerUserKey } from './complianceComposerStorage';

const STORAGE_PREFIX = 'lodgefirst_compliance_nav_v1';

/** Old Get started URL — kept so /compliance/start and saved nav still resolve. */
export const COMPLIANCE_NAV_START = 'start';
export const COMPLIANCE_NAV_NEW = 'new';

/** URL segment under /back-office/compliance — new | inbox id. */
export type ComplianceNavItemId = typeof COMPLIANCE_NAV_NEW | string;

const storageKey = (): string | null => {
  const userKey = complianceComposerUserKey();
  if (!userKey) return null;
  const organizationId = (typeof localStorage !== 'undefined'
    && localStorage.getItem('organization_data_id'))
    || 'default';
  return `${STORAGE_PREFIX}:${organizationId}:${userKey}`;
};

export const sanitizeComplianceNavItem = (raw: unknown): ComplianceNavItemId => {
  const value = String(raw || '').trim();
  // Get started is hidden — old /start bookmarks and localStorage land on New report.
  if (!value || value === COMPLIANCE_NAV_START) return COMPLIANCE_NAV_NEW;
  if (value === COMPLIANCE_NAV_NEW) return value;
  // Inbox ids are numeric strings from manager_reports_id_seq.
  if (/^[A-Za-z0-9_-]{1,80}$/.test(value)) return value;
  return COMPLIANCE_NAV_NEW;
};

export const loadComplianceNavItem = (): ComplianceNavItemId => {
  try {
    if (typeof localStorage === 'undefined') return COMPLIANCE_NAV_NEW;
    const key = storageKey();
    if (!key) return COMPLIANCE_NAV_NEW;
    return sanitizeComplianceNavItem(localStorage.getItem(key));
  } catch {
    return COMPLIANCE_NAV_NEW;
  }
};

export const saveComplianceNavItem = (navItem: ComplianceNavItemId): void => {
  try {
    if (typeof localStorage === 'undefined') return;
    const key = storageKey();
    if (!key) return;
    localStorage.setItem(key, sanitizeComplianceNavItem(navItem));
  } catch {
    // Quota / private mode — in-memory nav still works until refresh.
  }
};

export const isComplianceInboxNavItem = (navItem: ComplianceNavItemId): boolean => (
  navItem !== COMPLIANCE_NAV_START
  && navItem !== COMPLIANCE_NAV_NEW
  && !isCompliancePeekNavItem(navItem)
);

const PEEK_NAV_RE = /^peek-([A-Za-z0-9_-]{1,40})$/;

/** Temporary tab to read a lower-permission staff report before closing it. */
export const isCompliancePeekNavItem = (navItem: ComplianceNavItemId): boolean => (
  PEEK_NAV_RE.test(String(navItem || ''))
);

export const compliancePeekNavItem = (reportId: string): string => (
  `peek-${String(reportId || '').trim()}`
);

export const compliancePeekIdFromNav = (navItem: ComplianceNavItemId): string => {
  const match = String(navItem || '').match(PEEK_NAV_RE);
  return match ? match[1] : '';
};

/** How long a filed or dismissed item stays before it is dropped. */
export const REVIEWED_REPORT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Remaining time until a filed or dismissed item is dropped (7 days after mark). */
export const reviewedReportDeleteHint = (reviewedAt: string | null | undefined): string => {
  if (!reviewedAt) return '';
  const end = new Date(reviewedAt).getTime() + REVIEWED_REPORT_TTL_MS;
  const ms = end - Date.now();
  if (!Number.isFinite(end) || ms <= 0) return 'Deleting soon';
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const mins = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
  if (days >= 1) return `Deletes in ${days}d`;
  if (hours >= 1) return `Deletes in ${hours}h`;
  return `Deletes in ${Math.max(1, mins)}m`;
};
